package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

private const val PHONE_SHADOW = "drop-shadow(0 24px 32px rgba(30,40,52,.3))"

// ---------- Hero ----------

val PortalHeroGridStyle = CssStyle {
    base { grid("1fr").alignItems(AlignItems.Center).css("gap" to "clamp(24px, 4vw, 56px)") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "1.1fr .9fr") }
}

val PortalFreeStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(10.px).color(RdColor.Muted).css("flex-wrap" to "wrap", "font-size" to ".95rem") }
    cssRule(" b") {
        Modifier.backgroundColor(RdColor.Brand).color(RdColor.BrandInk).borderRadius(999.px)
            .css("font-weight" to 600, "padding" to "7px 12px")
    }
}

/**
 * One phone with job updates lighting up around it. On phones the updates
 * drop into a 2x2 grid under the screenshot instead of floating.
 */
val PortalLiveStyle = CssStyle {
    base { grid("1fr 1fr").gap(10.px).css("min-width" to "0", "padding-top" to "34px", "position" to "relative") }
    Breakpoint.MD { Modifier.css("display" to "grid", "grid-template-columns" to "1fr", "place-items" to "center", "min-height" to "520px", "padding-top" to "0") }
}

/** The hero clips its decorative rings sideways, so they never make the page scroll. */
val PortalHeroClipStyle = CssStyle.base {
    Modifier.css("overflow-x" to "clip")
}

/** Pale-blue circle with two dashed rings behind the phone. */
val PortalGlowStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD {
        Modifier.display(DisplayStyle.Block).position(Position.Absolute).border(1.px, LineStyle.Solid, RdColor.LogoBlue)
            .css("width" to "min(440px, 92%)", "aspect-ratio" to "1", "border-radius" to "50%",
                "background" to "radial-gradient(circle, rgb(230,237,245) 0 58%, transparent 59%)")
    }
    cssRule("::before") { Modifier.css("content" to "\"\"", "position" to "absolute", "inset" to "-9%", "border-radius" to "50%", "border" to "1px dashed rgb(180,198,220)") }
    cssRule("::after") { Modifier.css("content" to "\"\"", "position" to "absolute", "inset" to "-20%", "border-radius" to "50%", "border" to "1px dashed rgb(180,198,220)", "opacity" to .55) }
}

val PortalLivePhoneStyle = CssStyle {
    base { Modifier.css("grid-column" to "1 / -1", "justify-self" to "center", "position" to "relative", "z-index" to 1, "width" to "min(220px, 70%)", "height" to "auto", "filter" to PHONE_SHADOW) }
    Breakpoint.MD { Modifier.css("width" to "min(250px, 56%)") }
}

val PortalStatusStyle = CssStyle.base {
    Modifier.position(Position.Absolute).backgroundColor(RdColor.Brand).color(RdColor.BrandInk).borderRadius(999.px)
        .css("z-index" to 2, "top" to "-4px", "left" to "50%", "transform" to "translateX(-50%)", "white-space" to "nowrap",
            "padding" to "8px 12px", "font" to "500 .7rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase")
}

val PortalEventStyle = CssStyle {
    base {
        grid("30px 1fr").alignItems(AlignItems.Center).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(14.px).css("gap" to "2px 12px", "padding" to "10px", "z-index" to 2, "opacity" to .62, "transform" to "scale(.96)",
                "box-shadow" to RdSize.SHADOW, "transition" to "opacity .45s ease, transform .45s ease, border-color .45s ease")
    }
    Breakpoint.MD { Modifier.css("position" to "absolute", "width" to "min(250px, 58%)", "grid-template-columns" to "38px 1fr", "padding" to "12px 14px") }
    cssRule(" b") { Modifier.css("font-size" to ".92rem", "line-height" to 1.25) }
    cssRule(" > span:last-child") { Modifier.color(RdColor.Muted).css("font-size" to ".8rem", "line-height" to 1.3) }
}

val PortalEventActiveStyle = CssStyle {
    base { Modifier.css("opacity" to 1, "transform" to "scale(1.04)", "border-color" to "rgb(46,75,110)") }
    cssRule(" .portal-event-icon") { Modifier.backgroundColor(RdColor.Brand).color(RdColor.BrandInk) }
}

val PortalEventIconStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.SkySoft).color(RdColor.Brand).borderRadius(10.px)
            .css("grid-row" to "span 2", "width" to "30px", "height" to "30px", "place-items" to "center", "transition" to "background .45s, color .45s")
    }
    Breakpoint.MD { Modifier.css("width" to "38px", "height" to "38px") }
}

/** Where each of the four updates floats around the phone on wider screens. */
val PortalEventTopLeftStyle = CssStyle { Breakpoint.MD { Modifier.css("top" to "6%", "left" to "0") } }
val PortalEventUpperRightStyle = CssStyle { Breakpoint.MD { Modifier.css("top" to "26%", "right" to "0") } }
val PortalEventLowerLeftStyle = CssStyle { Breakpoint.MD { Modifier.css("bottom" to "24%", "left" to "0") } }
val PortalEventBottomRightStyle = CssStyle { Breakpoint.MD { Modifier.css("bottom" to "4%", "right" to "0") } }

// ---------- Job journey ----------

val PortalJourneyGridStyle = CssStyle {
    base { grid("1fr").css("gap" to "clamp(32px, 5vw, 72px)", "align-items" to "start") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "1fr 300px") }
}

/** Numbered steps on a vertical line; the numbers come from a CSS counter. */
val PortalJourneyStyle = CssStyle {
    base { Modifier.position(Position.Relative).css("list-style" to "none", "padding" to "0", "margin" to "0", "counter-reset" to "stage", "min-width" to "0") }
    cssRule("::before") {
        Modifier.position(Position.Absolute).backgroundColor(RdColor.LogoBlue)
            .css("content" to "\"\"", "left" to "21px", "top" to "8px", "bottom" to "8px", "width" to "2px")
    }
    cssRule(" > li") { Modifier.position(Position.Relative).css("display" to "grid", "grid-template-columns" to "44px 1fr", "gap" to "20px", "padding-bottom" to "36px") }
    cssRule(" > li:last-child") { Modifier.css("padding-bottom" to "0") }
    cssRule(" > li::before") {
        Modifier.position(Position.Relative).display(DisplayStyle.Grid).backgroundColor(RdColor.Paper).color(RdColor.Brand)
            .css("counter-increment" to "stage", "content" to "counter(stage)", "z-index" to 1, "width" to "44px", "height" to "44px",
                "border-radius" to "50%", "place-items" to "center", "border" to "2px solid rgb(46,75,110)", "font" to "800 1.05rem/1 ${RdFont.DISPLAY_STACK}")
    }
}

val PortalStageBodyStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(10.px).css("min-width" to "0", "padding-top" to "6px") }
    cssRule(" h3") { Modifier.css("font-size" to "1.3rem") }
    cssRule(" > p") { Modifier.color(RdColor.Muted).css("max-width" to "58ch") }
}

val PortalStageStatusStyle = CssStyle.base {
    Modifier.color(RdColor.Muted).css("font" to "500 .7rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase")
}

val PortalActionsStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap", "list-style" to "none", "padding" to "0", "margin-top" to "4px") }
    cssRule(" li") {
        Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(7.px).backgroundColor(RdColor.Paper)
            .border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(999.px).css("padding" to "7px 12px", "font-size" to ".88rem", "font-weight" to 500)
    }
    cssRule(" li::before") { Modifier.backgroundColor(RdColor.Brand).css("content" to "\"\"", "width" to "7px", "height" to "7px", "border-radius" to "50%") }
}

/** Phone screenshot that stays in view beside the steps on wide screens. */
val PortalJourneyPhoneStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(12.px).css("justify-items" to "center") }
    Breakpoint.LG { Modifier.position(Position.Sticky).css("top" to "calc(env(safe-area-inset-top, 0px) + 96px)") }
    cssRule(" img") { Modifier.css("width" to "100%", "max-width" to "280px", "height" to "auto", "filter" to PHONE_SHADOW) }
    cssRule(" small") { Modifier.color(RdColor.Muted).css("font-size" to ".85rem", "text-align" to "center") }
}

// ---------- Clients band ----------

val PortalClientsStyle = CssStyle {
    base { Modifier.backgroundColor(RdColor.Band).color(RdColor.BandText) }
    cssRule(" .rd-eyebrow") { Modifier.color(RdColor.BandMuted) }
    cssRule(" .rd-lede") { Modifier.color(RdColor.BandMuted) }
}

val PortalClientsGridStyle = CssStyle {
    base { grid("1fr").alignItems(AlignItems.Center).css("gap" to "clamp(32px, 5vw, 72px)") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to ".8fr 1.2fr") }
    cssRule(" img") { Modifier.css("width" to "100%", "max-width" to "300px", "height" to "auto", "justify-self" to "center", "border-radius" to "28px") }
}

val PortalStatsStyle = CssStyle {
    base { grid("1fr").gap(14.px) }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "1fr 1fr") }
    cssRule(" > div") { Modifier.display(DisplayStyle.Grid).gap(4.px).borderRadius(14.px).css("padding" to "16px 18px", "border" to "1px solid rgba(180,198,220,.35)") }
    cssRule(" b") { Modifier.color(RdColor.BandText).fontFamily(*RdFont.DISPLAY).css("font-size" to "1.05rem", "font-weight" to 800, "font-stretch" to "108%", "line-height" to 1.2) }
    cssRule(" span") { Modifier.color(RdColor.BandMuted).css("font-size" to ".92rem") }
}

// ---------- How to get it ----------

val PortalGetStepsStyle = CssStyle {
    base { grid("1fr").gap(20.px).css("list-style" to "none", "padding" to "0", "margin" to "0") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "repeat(3, 1fr)") }
    cssRule(" li") {
        Modifier.display(DisplayStyle.Grid).gap(10.px).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(16.px).css("align-content" to "start", "padding" to "26px")
    }
    // The middle step, the free months, stands out.
    cssRule(" li:nth-child(2)") { Modifier.backgroundColor(RdColor.SkySoft).css("border" to "2px solid rgb(46,75,110)") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".96rem") }
}

val PortalStepLabelStyle = CssStyle.base {
    Modifier.color(RdColor.Brand).css("font" to "500 .72rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase")
}

val PortalGetFootStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween).color(RdColor.Muted)
        .css("flex-wrap" to "wrap", "gap" to "12px 24px", "margin-top" to "22px")
}
